import "./style.css";
import * as dat from "lil-gui";
import * as THREE from "three";
import { BasisTextureLoader } from "three/examples/jsm/loaders/BasisTextureLoader.js";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { DragControls } from "three/examples/jsm/controls/DragControls.js";
import { CircleBufferGeometry, MathUtils } from "three";
import { CustomOrbitControls } from "./CustomOrbitControls";

let CubeExponent = 4;
let CubeSize = Math.pow(2, CubeExponent);
let GridUnits = 40 * (20 / CubeSize);
const OriginalGridSize = CubeSize * GridUnits;
let ctx;
let undoStack = [];
let previousZoom = 1;
const imageCanvas = document.getElementById("picture");
const interfaceSvg = document.getElementById("interface");
const svgOrigin = document.getElementById("origin");
const svgP1 = document.getElementById("s1");
const svgP2 = document.getElementById("s2");
const svgLine1 = document.getElementById("l1");
const svgLine2 = document.getElementById("l2");
const svgTriangle1 = document.getElementById("t1");
const svgTriangle2 = document.getElementById("t2");
const svgTriangleOrigin = document.getElementById("torigin");
let pointerDownEvent = null;
let isPointerDown = false;
let originOnScreenAtPointerDown = null;
let canvasOffsetAtPointerDown = null;
let canvasOffsetX = 0,
  canvasOffsetY = 0;
let canvasOffsetHomeX = 0,
  canvasOffsetHomeY = 0;
let imgData = false;
let imgDataRenderConf = {};
let didRotate = false;
const CtrlKey = 17,
  CmdKey = 91,
  ShiftKey = 16,
  DeleteKey = 8,
  KeyZ = 90,
  KeyQ = 81,
  OptionKey = 18,
  KeyR = 82,
  KeyP = 80,
  KeyS = 83,
  KeyW = 87,
  KeyD = 68,
  KeyV = 86,
  KeyC = 67;

const keyDownMap = {};

const EditMode = {
  VERTEX: 0,
  ALGO: 1,
  PREVIEW: 2,
  VOXELS: 3,
};
let editMode = EditMode.VOXELS;

function isInAlgoMode() {
  return editMode === EditMode.ALGO;
}

function isInVertexMode() {
  return editMode === EditMode.VERTEX;
}

function isInPreviewMode() {
  return editMode === EditMode.PREVIEW;
}

function isInVoxelMode() {
  return editMode === EditMode.VOXELS;
}
let camera, scene, controls, renderer;
let plane;
let gui;
let pointer, raycaster;

let rollOverMesh, rollOverMaterial;
let cubeGeo, cubeMaterial, pendingCubeMaterial;
let currentColor = null;

let guiData = {
  gridUnits: GridUnits,
  cubeSize: CubeSize,
  opacity: 0.5,
  x: 0,
  y: 0,
  z: 0,
  width: GridUnits * CubeSize,
  height: GridUnits * CubeSize,
};

let gridHelper = new THREE.GridHelper(CubeSize * GridUnits, GridUnits);

let isDragging = false,
  isDown = false;

let ImageScale = 1;

const frustumSize = 500;
const NORMAL_RIGHT = new THREE.Vector3().set(1, 0, 0);
const NORMAL_LEFT = new THREE.Vector3().set(0, 0, -1);

const TriangleWidth = 30;
const TriangleOffsetX = -TriangleWidth / 2,
  TriangleOffsetY = -TriangleWidth + TriangleWidth / 3;

let currentSvgElem = null;

let imageMinX = 0,
  imageMaxX = 0,
  imageMinY = 0,
  imageMaxY = 0;

const objects = [];
let deletableObjects = [],
  deletedObjects = [];
const voxelMap = {};
window.voxelMap = voxelMap;

let allAxes = ["x", "z"];
let newObjects = [];
let newObjectColorMap = {};
let currentAxis = "x";
let currentAxisIndex = 0;
let startPosition,
  lastPosition = null;

let currentImage = undefined;
let currentImageWidth = 0,
  currentImageHeight = 0;

let cameraHome = undefined;
let cameraRotated = undefined;

let OriginScreen = new THREE.Vector2(823.7421875, 1017.12109375); // new THREE.Vector2(1079.6875, 862.01171875);
let Right = new THREE.Vector2(1136.75390625, 862.8203125) //new THREE.Vector2(1135.15234375, 832.47265625)
  .sub(OriginScreen)
  .normalize();
let Left = new THREE.Vector2(622.41015625, 967.93359375) // new THREE.Vector2(997.04296875, 844.2890625)
  .sub(OriginScreen)
  .normalize();

let LeftRightAngle = Math.acos(
  Right.dot(Left) / (Right.length() * Left.length())
);

init();
render();

function init() {
  document.getElementById("btnRunAlgo").addEventListener("click", () => {
    setEditMode(EditMode.ALGO);
    setTimeout(() => {
      runAngleAlgo();
    }, 5);
  });
  document.getElementById("btnIncreaseDetail").addEventListener("click", () => {
    CubeExponent = Math.max(2, CubeExponent - 1);
    udpateCubeSize(Math.pow(2, CubeExponent));
  });
  document.getElementById("btnDecreaseDetail").addEventListener("click", () => {
    CubeExponent = Math.min(6, CubeExponent + 1);
    udpateCubeSize(Math.pow(2, CubeExponent));
  });
  document.getElementById("btnReset").addEventListener("click", () => {
    resetCameraPosition();
  });

  const aspect = window.innerWidth / window.innerHeight;
  camera = new THREE.OrthographicCamera(
    (frustumSize * aspect) / -2,
    (frustumSize * aspect) / 2,
    frustumSize / 2,
    frustumSize / -2,
    1,
    2000
  );
  camera.position.set(-400, 400, 400);
  camera.lookAt(0, 0, 0);
  cameraHome = camera.clone();

  scene = new THREE.Scene();
  //  scene.background = new THREE.Color(0xf0f0f0);

  // roll-over helpers

  const rollOverGeo = new THREE.BoxGeometry(CubeSize, CubeSize, CubeSize);
  rollOverMaterial = new THREE.MeshBasicMaterial({
    color: 0xff0000,
    opacity: 0.5,
    transparent: true,
  });
  rollOverMesh = new THREE.Mesh(rollOverGeo, rollOverMaterial);
  scene.add(rollOverMesh);

  // cubes

  cubeGeo = new THREE.BoxGeometry(CubeSize, CubeSize, CubeSize);
  cubeMaterial = new THREE.MeshLambertMaterial({
    color: 0xdddddd,
    //map: new THREE.TextureLoader().load("textures/square-outline-textured.png"),
  });
  pendingCubeMaterial = new THREE.MeshLambertMaterial({
    color: 0xfeb74c,
    opacity: 0.5,
    transparent: true,
    //map: new THREE.TextureLoader().load("textures/square-outline-textured.png"),
  });

  // grid

  scene.add(gridHelper);

  //

  raycaster = new THREE.Raycaster();
  pointer = new THREE.Vector2();

  const geometry = new THREE.PlaneGeometry(
    CubeSize * GridUnits,
    CubeSize * GridUnits
  );
  geometry.rotateX(-Math.PI / 2);

  plane = new THREE.Mesh(
    geometry,
    new THREE.MeshBasicMaterial({
      visible: false,
    })
  );
  scene.add(plane);

  const ambientLight = new THREE.AmbientLight(0xffffff);
  scene.add(ambientLight);

  const directionalLight = new THREE.DirectionalLight(0xffffff, 0.1);
  directionalLight.position.set(1, 0.75, 0.5).normalize();
  scene.add(directionalLight);
  scene.background = null;

  renderer = new THREE.WebGLRenderer({
    antialias: true,
    alpha: true,
    preserveDrawingBuffer: true,
  });
  renderer.setPixelRatio(Math.min(2, window.devicePixelRatio));
  renderer.setSize(window.innerWidth, window.innerHeight);
  document.body.appendChild(renderer.domElement);

  document.addEventListener("pointermove", onPointerMove);
  document.addEventListener("pointerdown", onPointerDown);
  document.addEventListener("pointerup", onPointerUp);
  document.addEventListener("keydown", onKeyDown);
  document.addEventListener("keyup", onKeyUp);
  document.addEventListener("keydown", function (event) {
    if ((event.metaKey || event.ctrlKey) && event.key === "z") {
      performUndo();
      keyDownMap[KeyZ] = false;
      event.preventDefault();
      event.stopPropagation();
    }
    if ((event.metaKey || event.ctrlKey) && event.key === "s") {
      if (isInVoxelMode()) {
        // draw to canvas...
        //const blob = renderer.domElement.toDataURL("image/png");

        gridHelper.visible = false;
        rollOverMesh.visible = false;
        render();
        var dataURL = renderer.domElement.toDataURL();
        const link = document.createElement("a");
        link.download = `tracy-${new Date().getTime()}.png`;
        link.href = dataURL;
        link.target = "_blank";
        link.click();

        gridHelper.visible = true;
        rollOverMesh.visible = getRollOverMeshVisible();

        render();

        //saveAs(blob, "tracy.png");
      }

      event.preventDefault();
      event.stopPropagation();
    }

    updateUI();
  });

  //

  window.addEventListener("resize", onWindowResize);

  resetControls();

  updateUI();

  ctx = imageCanvas.getContext("2d");

  function loadImageByUrl(url) {
    const remoteImage = new Image();
    remoteImage.onload = () => {
      setCurrentImage(remoteImage);
    };
    remoteImage.src = url;
  }

  loadImageByUrl("/textures/cat.jpg");

  function setCurrentImage(newImage) {
    imgDataRenderConf = {};
    if (newImage) {
      resetCameraPosition();
      if (
        newImage.width >= window.innerWidth - 200 ||
        newImage.height >= window.innerHeight - 200
      ) {
        const newWidth = Math.min(newImage.width, window.innerWidth - 200);
        const newHeight = Math.min(newImage.height, window.innerHeight - 200);
        const scaleWidth = newWidth / newImage.width;
        const scaleHeight = newHeight / newImage.height;
        ImageScale = Math.min(scaleWidth, scaleHeight);
      } else {
        ImageScale = 1;
      }
      currentImageWidth = newImage.width * ImageScale;
      currentImageHeight = newImage.height * ImageScale;
    }
    currentImage = newImage;
    drawCurrentImage();
    if (currentImage) {
      initSvg();
      setEditMode(EditMode.VERTEX);
    }
    deleteAllVoxels();
  }

  window.addEventListener("resize", resizeCanvas);
  resizeCanvas();

  document.addEventListener("pointermove", (event) => {
    var x = parseInt((event.clientX / window.innerWidth) * imageCanvas.width);
    var y = parseInt((event.clientY / window.innerHeight) * imageCanvas.height);

    currentColor = getColorAt(x, y);

    return;

    if (
      imgData &&
      x >= imageMinX &&
      x < imageMaxX &&
      y >= imageMinY &&
      y < imageMaxY
    ) {
      let index = (y * imgData.width + x) * 4;

      let p = imgData.data;

      const red = p[index + 0];
      const green = p[index + 1];
      const blue = p[index + 2];
      const alpha = p[index + 3];

      currentColor = new THREE.Color(red / 255.0, green / 255.0, blue / 255.0);
    } else {
      currentColor = null;
    }
  });

  let dropArea = document.body;

  ["dragenter", "dragover", "dragleave", "drop"].forEach((eventName) => {
    dropArea.addEventListener(eventName, preventDefaults, false);
  });

  function preventDefaults(e) {
    e.preventDefault();
    e.stopPropagation();
  }

  ["dragenter", "dragover"].forEach((eventName) => {
    dropArea.addEventListener(eventName, highlight, false);
  });
  ["dragleave", "drop"].forEach((eventName) => {
    dropArea.addEventListener(eventName, unhighlight, false);
  });

  function highlight(e) {
    dropArea.classList.add("highlight");
  }

  function unhighlight(e) {
    dropArea.classList.remove("highlight");
  }

  dropArea.addEventListener("drop", handleDrop, false);

  function handleDrop(e) {
    let dt = e.dataTransfer;
    let files = dt.files;

    [...files].forEach(previewFile);
  }

  function previewFile(file) {
    let reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = function () {
      let img = new Image();
      img.onload = function () {
        // access image size here
        setCurrentImage(img);
      };
      img.onerror = function () {
        setCurrentImage(null);
      };
      img.src = reader.result;
    };
  }
}

function parsePixels(pxString) {
  return parseFloat(pxString.replace("px", ""));
}

function getSvgPosition(elem) {
  return {
    x: parsePixels(elem.getAttribute("cx")),
    y: parsePixels(elem.getAttribute("cy")),
  };
}
function toVec2({ x, y }) {
  return new THREE.Vector2(x, y);
}

function getCornerPoints() {
  return {
    origin: getCssPosition(svgTriangleOrigin, TriangleWidth / 2, 0),
    right: getCssPosition(svgTriangle1, TriangleWidth / 2, 0),
    left: getCssPosition(svgTriangle2, TriangleWidth / 2, 0),
  };
}

function updateSvgLines() {
  const origin = getSvgPosition(svgOrigin);
  const p1 = getSvgPosition(svgP1);
  const p2 = getSvgPosition(svgP2);

  setPixelAttribute(svgLine1, "x1", origin.x);
  setPixelAttribute(svgLine1, "y1", origin.y);

  setPixelAttribute(svgLine1, "x2", p1.x);
  setPixelAttribute(svgLine1, "y2", p1.y);

  setPixelAttribute(svgLine2, "x1", origin.x);
  setPixelAttribute(svgLine2, "y1", origin.y);

  setPixelAttribute(svgLine2, "x2", p2.x);
  setPixelAttribute(svgLine2, "y2", p2.y);

  setCssPosition(svgTriangle1, p1.x + TriangleOffsetX, p1.y + TriangleOffsetY);
  setCssPosition(svgTriangle2, p2.x + TriangleOffsetX, p2.y + TriangleOffsetY);
  setCssPosition(
    svgTriangleOrigin,
    origin.x + TriangleOffsetX,
    origin.y + TriangleOffsetY
  );
}

function getCssPosition(elem, dx, dy) {
  return {
    x: parsePixels(elem.style.left) + (dx || 0),
    y: parsePixels(elem.style.top) + (dy || 0),
  };
}

function setCssPosition(elem, x, y) {
  elem.style.left = `${x}px`;
  elem.style.top = `${y}px`;
}

function setPixelAttribute(elem, attr, value) {
  elem.setAttribute(attr, `${value}px`);
}

function getScreenCenter(width, height) {
  return {
    x: (window.innerWidth - (width || 0)) / 2,
    y: (window.innerHeight - (height || 0)) / 2,
  };
}

function setSvgPosition(elem, position) {
  setPixelAttribute(elem, "cx", position.x);
  setPixelAttribute(elem, "cy", position.y);
}

let didAdjustNonOrigin = false;
let isFirstInit = true;
function initSvg() {
  if (!currentImage) {
    return;
  }
  didAdjustNonOrigin = false;

  // position at center

  const originRadius = parsePixels(svgOrigin.getAttribute("r"));

  let originCenter = getScreenCenter();

  originCenter.y = Math.min(
    window.innerHeight - 400,
    originCenter.y + currentImageHeight / 3
  );

  const xDistance = currentImageWidth * 0.2;
  const yDistance = xDistance * Math.tan(MathUtils.degToRad(30));

  let p1Position = {
    x: originCenter.x + xDistance,
    y: originCenter.y - yDistance,
  };
  let p2Position = {
    x: originCenter.x - xDistance,
    y: originCenter.y - yDistance,
  };

  // DEBUG
  //originCenter = { x: 1028.41796875, y: 831.640625 };
  //p1Position = { x: 1204.3671875, y: 745.45703125 };
  //p2Position = { x: 912.71875, y: 805.69140625 };

  setSvgPosition(svgOrigin, originCenter);
  setSvgPosition(svgP1, p1Position);
  setSvgPosition(svgP2, p2Position);

  updateSvgLines();

  if (isFirstInit) {
    isFirstInit = false;
    const allSvgs = [svgOrigin, svgP1, svgP2];
    allSvgs.forEach((s) => {
      s.addEventListener("pointerdown", (event) => {
        currentSvgElem = event.target;
        document.body.style.cursor = "move";
      });
    });

    document.addEventListener("pointerup", (event) => {
      currentSvgElem = null;
      document.body.style.cursor = "";
    });

    document.addEventListener("pointermove", (event) => {
      if (!currentSvgElem) {
        return;
      }
      const before = getSvgPosition(currentSvgElem);
      const d = { x: event.clientX, y: event.clientY };
      setPixelAttribute(currentSvgElem, "cx", d.x);
      setPixelAttribute(currentSvgElem, "cy", d.y);

      const origin = getSvgPosition(svgOrigin);
      const newPosition = getSvgPosition(currentSvgElem);

      const svgAngle = MathUtils.radToDeg(getSvgAngle());
      const svgAngleToOrigin = MathUtils.radToDeg(getSvgAngleToOrigin(d));

      let canMove = true;

      if (svgAngleToOrigin < 0 || svgAngleToOrigin > 120) {
        canMove = false;
      }

      const isMovingOrigin = currentSvgElem === svgOrigin;

      if (currentSvgElem === svgP1) {
        if (newPosition.x <= origin.x) {
          canMove = false;
        }
      } else if (currentSvgElem === svgP2) {
        if (newPosition.x >= origin.x) {
          canMove = false;
        }
      } else {
        // origin

        if (canMove && !didAdjustNonOrigin) {
          const delta = {
            x: newPosition.x - before.x,
            y: newPosition.y - before.y,
          };
          for (const elem of [svgP1, svgP2]) {
            const elemPos = getSvgPosition(elem);
            setPixelAttribute(elem, "cx", elemPos.x + delta.x);
            setPixelAttribute(elem, "cy", elemPos.y + delta.y);
          }
        }
      }

      if (canMove) {
        if (!isMovingOrigin) {
          didAdjustNonOrigin = true;
        }
        updateSvgLines();
      } else {
        setSvgPosition(currentSvgElem, before);
      }
    });
  }
}

function getSvgAngleToOrigin(position) {
  const origin = getSvgPosition(svgOrigin);
  const elevatedOrigin = { x: origin.x, y: origin.y - 100 };

  const threeOrigin = new THREE.Vector2(origin.x, origin.y);
  const threeP1 = new THREE.Vector2(position.x, position.y);
  const threeP2 = new THREE.Vector2(elevatedOrigin.x, elevatedOrigin.y);

  var px1 = threeP1.clone().sub(threeOrigin).normalize();

  var px2 = threeP2.clone().sub(threeOrigin).normalize();

  return Math.acos(px1.dot(px2) / (px1.length() * px2.length()));
}

function getSvgAngle() {
  const origin = getSvgPosition(svgOrigin);
  const p1 = getSvgPosition(svgP1);
  const p2 = getSvgPosition(svgP2);

  const threeOrigin = new THREE.Vector2(origin.x, origin.y);
  const threeP1 = new THREE.Vector2(p1.x, p1.y);
  const threeP2 = new THREE.Vector2(p2.x, p2.y);

  var px1 = threeP1.clone().sub(threeOrigin).normalize();

  var px2 = threeP2.clone().sub(threeOrigin).normalize();

  return Math.acos(px1.dot(px2) / (px1.length() * px2.length()));
}

function getColorAt(x, y) {
  if (isResetVisible()) {
    return null;
  }
  x = parseInt(x);
  y = parseInt(y);
  if (
    imgData &&
    !Number.isNaN(x) &&
    !Number.isNaN(y) &&
    x >= imageMinX &&
    x < imageMaxX &&
    y >= imageMinY &&
    y < imageMaxY
  ) {
    let index = (y * imgData.width + x) * 4;

    let p = imgData.data;

    const red = p[index + 0];
    const green = p[index + 1];
    const blue = p[index + 2];
    const alpha = p[index + 3];

    if (red === 0 && green === 0 && blue === 0 && alpha === 0) {
      return null;
    }

    return new THREE.Color(red / 255.0, green / 255.0, blue / 255.0);
  } else {
    return null;
  }
}

/*
function updateDivisions(value) {
  let corners = getCornerPoints();

  let originOnPlane = getScreenPointOnPlane(corners.origin);
  let rightOnPlane = getScreenPointOnPlane(corners.right);

  if (originOnPlane && rightOnPlane) {
    const distance = originOnPlane.distanceTo(rightOnPlane);

    let newCubeSizeEven = Math.round(distance / value);
    newCubeSizeEven = newCubeSizeEven - (newCubeSizeEven % 4);

    const delta = newCubeSizeEven * value - distance;

    let newCubeSize = Math.max(4, newCubeSizeEven);
    let newGrindUnits = newCubeSize * 2;

    CubeSize = newCubeSize;
    GridUnits = newGrindUnits;

    let growthFactor = (CubeSize * GridUnits) / OriginalGridSize;

    scene.remove(gridHelper);
    gridHelper = new THREE.GridHelper(
      (CubeSize * GridUnits) / growthFactor,
      GridUnits / growthFactor
    );
    scene.add(gridHelper);

    scene.remove(rollOverMesh);

    const rollOverGeo = new THREE.BoxGeometry(CubeSize, CubeSize, CubeSize);
    rollOverMesh = new THREE.Mesh(rollOverGeo, rollOverMaterial);

    cubeGeo = new THREE.BoxGeometry(CubeSize, CubeSize, CubeSize);

    scene.add(rollOverMesh);

    render();
  }
}*/

function udpateCubeSize(value) {
  let newCubeSizeEven = value - (value % 4);

  let newCubeSize = Math.max(4, newCubeSizeEven);
  let newGrindUnits = newCubeSize * 2;

  CubeSize = newCubeSize;
  GridUnits = newGrindUnits;

  let growthFactor = (CubeSize * GridUnits) / OriginalGridSize;

  scene.remove(gridHelper);
  gridHelper = new THREE.GridHelper(
    (CubeSize * GridUnits) / growthFactor,
    GridUnits / growthFactor
  );
  scene.add(gridHelper);

  scene.remove(rollOverMesh);

  const rollOverGeo = new THREE.BoxGeometry(CubeSize, CubeSize, CubeSize);
  rollOverMesh = new THREE.Mesh(rollOverGeo, rollOverMaterial);

  cubeGeo = new THREE.BoxGeometry(CubeSize, CubeSize, CubeSize);

  scene.add(rollOverMesh);

  render();
}

function getVoxelKey(voxelPosition) {
  return `${voxelPosition.x};${voxelPosition.y};${voxelPosition.z}`;
}

function removeFromScene(voxel, hardDelete) {
  const key = getVoxelKey(voxel.position);
  if (voxelMap[key]) {
    delete voxelMap[key];
    if (hardDelete) {
      deletedObjects.push(voxel);
    }
  }
  if (objects.indexOf(voxel) !== -1) {
    objects.splice(objects.indexOf(voxel), 1);
  }
  scene.remove(voxel);
  updateDetailButtons();
}

function updateDetailButtons() {
  let canChangeVoxelDetail =
    Object.values(voxelMap).length === 0 && isInVoxelMode();
  document.getElementById("voxel-buttons").style.display = canChangeVoxelDetail
    ? "block"
    : "none";
}

function addToUndoStack() {
  undoStack.push({ ...voxelMap });
  window.undoStack = undoStack;
}

function deleteAllVoxels() {
  for (const obj of Object.values(voxelMap)) {
    removeFromScene(obj, true);
    if (newObjects.indexOf(obj) !== -1) {
      newObjects.splice(newObjects.indexOf(obj), 1);
    }
  }
}

function performUndo() {
  let previousState = undoStack.pop();
  if (previousState) {
    deleteAllVoxels();
  }
  if (undoStack.length) {
    previousState = undoStack[undoStack.length - 1];
    for (const obj of Object.values(previousState)) {
      addVoxelToScene(obj);
    }
  }

  render();
}

function addVoxelToScene(voxel) {
  const key = getVoxelKey(voxel.position);
  const existingVoxel = voxelMap[key];
  if (!existingVoxel) {
    voxelMap[key] = voxel;
    scene.add(voxel);
    objects.push(voxel);
    updateDetailButtons();
    return true;
  } else {
    existingVoxel.material = voxel.material;
    updateDetailButtons();
    return false;
  }
}

function onWindowResize() {
  const aspect = window.innerWidth / window.innerHeight;

  camera.left = (-frustumSize * aspect) / 2;
  camera.right = (frustumSize * aspect) / 2;
  camera.top = frustumSize / 2;
  camera.bottom = -frustumSize / 2;

  camera.updateProjectionMatrix();

  renderer.setSize(window.innerWidth, window.innerHeight);

  render();
}

let lastMoveEvent = null;

function onPointerMove(event) {
  lastMoveEvent = event;
  pointer.set(
    (event.clientX / window.innerWidth) * 2 - 1,
    -(event.clientY / window.innerHeight) * 2 + 1
  );

  raycaster.setFromCamera(pointer, camera);

  if (
    deletedObjects.length === 0 &&
    isKeyDown(KeyD) &&
    deletableObjects.length === 0
  ) {
    updateDeletableObjects();
  }

  const rayCasterObjects =
    isKeyDown(KeyD) && deletableObjects.length
      ? deletableObjects
      : [...objects, plane];

  const intersects = raycaster.intersectObjects(rayCasterObjects, false);

  let canUpdatePosition = true;

  if (intersects.length > 0) {
    const intersect = intersects[0];

    const obj = intersect.object;

    if (isKeyDown(KeyD) && obj !== plane) {
      if (
        isKeyDown(KeyD) &&
        deletableObjects.length === 0 &&
        deletedObjects.length === 0
      ) {
        updateDeletableObjects();
      }

      if (isDown) {
        // remove object

        let canRemove = true;
        let enforcedAxis = null;

        if (deletedObjects.length >= 1) {
          let maxDistanceX = Math.max(
            ...deletedObjects.map((d) =>
              Math.abs(d.position.x - startPosition.x)
            )
          );
          let maxDistanceZ = Math.max(
            ...deletedObjects.map((d) =>
              Math.abs(d.position.z - startPosition.z)
            )
          );

          if (maxDistanceX > CubeSize || maxDistanceZ > CubeSize) {
            //canRemove = false;
          }
          if (deletedObjects.length >= 2) {
            if (maxDistanceX > maxDistanceZ) {
              enforcedAxis = "z";
            } else {
              enforcedAxis = "x";
            }
          }
        }

        if (enforcedAxis != null) {
          if (obj.position[enforcedAxis] !== startPosition[enforcedAxis]) {
            canRemove = false;
          }
          if (deletedObjects.length === 2) {
            updateDeletableObjects(enforcedAxis);
          }
        }

        if (canRemove) {
          removeFromScene(obj, true);
          if (newObjects.indexOf(obj) !== -1) {
            newObjects.splice(newObjects.indexOf(obj), 1);
          }
        }
      }

      if (canUpdatePosition) {
        rollOverMesh.position.copy(obj.position);
      }
    } else {
      rollOverMesh.position.copy(intersect.point).add(intersect.face.normal);
      rollOverMesh.position
        .divideScalar(CubeSize)
        .floor()
        .multiplyScalar(CubeSize)
        .addScalar(CubeSize / 2);
    }

    if (isDragging) {
      updateObjects(pointer, rollOverMesh.position.clone());
    }
  }

  if (isKeyDown(OptionKey) && originOnScreenAtPointerDown) {
    let originOnScreenNow = toScreenPosition(new THREE.Vector3());

    let delta = {
      x: originOnScreenNow.x - originOnScreenAtPointerDown.x,
      y: originOnScreenNow.y - originOnScreenAtPointerDown.y,
    };
    canvasOffsetX += delta.x / camera.zoom;
    canvasOffsetY += delta.y / camera.zoom;
    originOnScreenAtPointerDown = originOnScreenNow;
  }

  render();
}

function sum(a, b) {
  return a + b;
}

function getMaterialAt(x, y) {
  const color = getColorAt(x, y);
  if (color != null) {
    return new THREE.MeshLambertMaterial({
      color,
    });
  } else {
    return cubeMaterial;
  }
}

function getCurrentMaterial() {
  if (currentColor != null) {
    return new THREE.MeshLambertMaterial({
      color: currentColor,
    });
  } else {
    return cubeMaterial;
  }
}

function calculateDistance(newPosition, axis) {
  const startingPoint = Math.min(startPosition[axis], newPosition[axis]);
  const endPoint = Math.max(startPosition[axis], newPosition[axis]);
  return Math.abs(endPoint - startingPoint);
}

function updateObjects(pointer, newPosition) {
  let axisToUse = currentAxis;
  let maxDistance = null;
  for (let a of allAxes) {
    let distance = calculateDistance(newPosition, a);
    if (maxDistance == null || distance > maxDistance) {
      axisToUse = a;
      maxDistance = distance;
    }
  }

  newObjects.map((obj) => {
    removeFromScene(obj);
  });

  newObjects = [];
  const startingPoint = Math.min(
    startPosition[axisToUse],
    newPosition[axisToUse]
  );
  let numAdded = 0;
  const endPoint = Math.max(startPosition[axisToUse], newPosition[axisToUse]);

  const startingPointIsFirst = !(startPosition[axisToUse] === startingPoint);

  for (let v = startingPoint; v <= endPoint; v += CubeSize) {
    numAdded += 1;

    let isFirst = v === startingPoint;
    let isLast = v + CubeSize > endPoint;

    let voxelPostion = startPosition.clone();
    voxelPostion[axisToUse] = v;

    const screenPos = toScreenPosition(voxelPostion);

    let voxelKey = getVoxelKey(voxelPostion);
    let material = newObjectColorMap[voxelKey];
    if (!material) {
      material = getMaterialAt(screenPos.x, screenPos.y);
      newObjectColorMap[voxelKey] = material;
    }

    const voxel = new THREE.Mesh(cubeGeo, material);
    voxel.userData.drag = true;
    voxel.position.copy(voxelPostion);

    const didAdd = addVoxelToScene(voxel);
    if (didAdd) {
      newObjects.push(voxel);
    }

    const isHovering =
      (isFirst && startingPointIsFirst) || (isLast && !startingPointIsFirst);

    if (false && isHovering) {
      raycaster.setFromCamera(pointer, camera);
      const intersects = raycaster.intersectObjects([voxel], false);
      if (intersects.length > 0) {
        //material = getCurrentMaterial();
        //newObjectColorMap[voxelKey] = material;
        //voxel.material = material;
        //console.log("INTERSECT");
      }
    }
  }
  lastPosition = newPosition;
  if (numAdded > 0) {
    rollOverMesh.visible = false;
  }
}

function resetMaterials() {
  for (const obj of objects) {
    if (obj !== rollOverMesh && obj !== plane) {
      //obj.material = cubeMaterial;
    }
  }
}

function onPointerUp(event) {
  isPointerDown = false;
  if (
    event.target === renderer.domElement &&
    startPosition.equals(rollOverMesh.position) &&
    isDragging &&
    !isKeyDown(KeyD) &&
    getScreenPointOnPlane({ x: event.clientX, y: event.clientY })
  ) {
    const voxel = new THREE.Mesh(cubeGeo, getCurrentMaterial());
    voxel.userData.drag = true;
    voxel.position.copy(rollOverMesh.position);
    const didAdd = addVoxelToScene(voxel);
    if (didAdd) {
      newObjects.push(voxel);
    }
  }

  addToUndoStack();
  originOnScreenAtPointerDown = null;
  isDragging = false;
  isDown = false;
  rollOverMesh.visible = getRollOverMeshVisible();
  newObjectColorMap = {};
  deletedObjects = [];
  resetMaterials();
  newObjects.map((obj) => {
    //obj.material = cubeMaterial;
  });
  if (lastMoveEvent != null) {
    onPointerMove(lastMoveEvent);
  }

  if (isKeyDown(KeyD)) {
    updateDeletableObjects();
  }

  if (isKeyDown(OptionKey)) {
    cameraHome = camera.clone();
    cameraHome.zoom = 1;
  }

  render();
}

function updateDeletableObjects(enforcedAxis) {
  deletableObjects = objects.filter(
    (obj) =>
      obj !== plane &&
      obj !== rollOverMesh &&
      obj.position.y === rollOverMesh.position.y &&
      (!enforcedAxis ||
        obj.position[enforcedAxis] === rollOverMesh.position[enforcedAxis])
  );
  const hasAdjacent = deletableObjects.filter(
    (obj) =>
      Math.abs(obj.position.z - rollOverMesh.position.z) <= CubeSize &&
      Math.abs(obj.position.x - rollOverMesh.position.x) <= CubeSize
  );
  if (!hasAdjacent.length) {
    deletableObjects = [];
  }
}

function updateOrigin() {
  originOnScreenAtPointerDown = toScreenPosition(new THREE.Vector3());
  canvasOffsetAtPointerDown = { x: canvasOffsetX, y: canvasOffsetY };
}

function onPointerDown(event) {
  pointerDownEvent = event;
  isPointerDown = event.target === renderer.domElement;
  updateOrigin();

  newObjects = [];
  startPosition = rollOverMesh.position.clone();
  lastPosition = rollOverMesh.position.clone();

  isDown = isInVoxelMode() && !isKeyDown(OptionKey);
  isDragging =
    isInVoxelMode() &&
    !isKeyDown(KeyR) &&
    !isKeyDown(OptionKey) &&
    !isKeyDown(KeyD) &&
    !isKeyDown(OptionKey) &&
    !isKeyDown(ShiftKey);
  pointer.set(
    (event.clientX / window.innerWidth) * 2 - 1,
    -(event.clientY / window.innerHeight) * 2 + 1
  );

  raycaster.setFromCamera(pointer, camera);

  const intersects = raycaster.intersectObjects(objects, false);

  if (intersects.length > 0) {
    const intersect = intersects[0];

    if (!isKeyDown(OptionKey)) {
      // delete cube

      if (isKeyDown(KeyD)) {
        if (intersect.object !== plane) {
          removeFromScene(intersect.object, true);
        }
      }
    }

    render();
  }
}

function render() {
  renderer.render(scene, camera);
  drawCurrentImage();
}

function isKeyDown(keyCode) {
  return keyDownMap[keyCode] === true;
}

function updateOrbitControlsEnabled() {
  controls.enabled =
    isKeyDown(OptionKey) ||
    isKeyDown(KeyR) ||
    isKeyDown(OptionKey) ||
    isKeyDown(ShiftKey);
  if (isKeyDown(KeyR)) {
    didRotate = true;
    controls.mouseButtons = {
      LEFT: THREE.MOUSE.ROTATE,
      MIDDLE: THREE.MOUSE.DOLLY,
      RIGHT: THREE.MOUSE.ROTATE,
    };
  } else if (isKeyDown(OptionKey) || isKeyDown(ShiftKey)) {
    controls.mouseButtons = {
      LEFT: THREE.MOUSE.PAN,
      MIDDLE: THREE.MOUSE.DOLLY,
      RIGHT: THREE.MOUSE.PAN,
    };
  } else {
    controls.mouseButtons = {
      LEFT: THREE.MOUSE.ROTATE,
      MIDDLE: THREE.MOUSE.DOLLY,
      RIGHT: THREE.MOUSE.PAN,
    }; // Touch fingers
  }

  controls.enablePan = isKeyDown(OptionKey) || isKeyDown(ShiftKey);
  controls.enableRotate = isKeyDown(KeyR);
}

function getScreenPointOnPlane({ x, y }) {
  const pointer = new THREE.Vector2();
  pointer.set(
    (x / window.innerWidth) * 2 - 1,
    -(y / window.innerHeight) * 2 + 1
  );
  raycaster.setFromCamera(pointer, camera);

  const intersects = raycaster.intersectObjects([plane], false);

  if (intersects.length > 0) {
    const intersect = intersects[0];
    intersect.point.y = plane.position.y;
    return intersect.point.clone();
  } else {
    return null;
  }
}

function isDebug() {
  return process.env.NODE_ENV === "development";
}

function onKeyUp(event) {
  const shiftWasDown = isKeyDown(OptionKey);

  keyDownMap[event.keyCode] = false;

  if (event.keyCode === KeyC && isDebug()) {
    setEditMode((editMode + 1) % Object.values(EditMode).length);
  }

  switch (event.keyCode) {
    case KeyQ:
      if (imageCanvas && isInVoxelMode()) {
        //imageCanvas.style.display = "block";
      }
      break;
    case KeyD:
      deletedObjects = [];
    case CtrlKey:
      break;
    case CmdKey:
      break;
    case OptionKey:
      break;
  }
  updateOrbitControlsEnabled();
  updateUI();

  if (isInVoxelMode()) {
    if (shiftWasDown !== isKeyDown(OptionKey)) {
      if (isKeyDown(OptionKey)) {
        updateOrigin();
        render();
      }
    }
  }
}

function onKeyDown(event) {
  keyDownMap[event.keyCode] = true;

  switch (event.keyCode) {
    case KeyQ:
      if (imageCanvas && isInVoxelMode()) {
        //imageCanvas.style.display = "none";
      }
      break;
    case KeyD:
      if (!isDragging) {
        // make copy of deletable objects
        updateDeletableObjects();
      }
    case CtrlKey:
      break;
    case CmdKey:
      break;
    case OptionKey:
      break;
    case DeleteKey:
      //resetCameraPosition();
      break;
    case KeyP:
      runAngleAlgo();

      break;
  }
  updateOrbitControlsEnabled();
  updateUI();
}

function resetControls() {
  controls = new CustomOrbitControls(camera, renderer.domElement);

  controls.enableZoom = true;
  controls.enablePan = true;
  controls.enableRotate = false;
  controls.enabled = false;
  previousZoom = camera.zoom;

  controls.addEventListener("change", (event) => {
    if (camera.zoom !== previousZoom) {
      if (isKeyDown(OptionKey) && isInVoxelMode()) {
        updateOrigin();
      }
      previousZoom = camera.zoom;
    }
    updateResetVisibility();
    render();
  });
}

function isResetVisible() {
  let hasDifferentRotation = false;
  if (isInVoxelMode()) {
    if (didRotate) {
      if (cameraRotated && !cameraRotated.rotation.equals(camera.rotation)) {
        hasDifferentRotation = true;
      } else if (cameraHome && !cameraHome.rotation.equals(camera.rotation)) {
        hasDifferentRotation = true;
      }
    }
  }
  return hasDifferentRotation;
}

function updateResetVisibility() {
  setResetVisible(isResetVisible());
}

function setResetVisible(flag) {
  document.getElementById("reset").style.display = flag ? "block" : "none";
  updateUI();
}

function resetCameraPosition() {
  camera.position.copy(cameraHome.position);
  if (cameraRotated) {
    camera.rotation.copy(cameraRotated.rotation);
    camera.zoom = 1;
    canvasOffsetX = canvasOffsetHomeX;
    canvasOffsetY = canvasOffsetHomeY;
  } else {
    camera.rotation.copy(cameraHome.rotation);
    camera.zoom = 1;

    canvasOffsetX = canvasOffsetHomeX;
    canvasOffsetY = canvasOffsetHomeY;
  }
  didRotate = false;
  camera.updateProjectionMatrix();
  resetControls();
  updateResetVisibility();
  render();
}

/* Find Angle code */
function calculateLengthOfNormals(cameraToUse) {
  let screenOrigin = toScreenPosition(new THREE.Vector3(0, 0, 0));
  let screenRight = toScreenPosition(
    NORMAL_RIGHT.clone().multiplyScalar(CubeSize)
  );
  let screenLeft = toScreenPosition(
    NORMAL_LEFT.clone().multiplyScalar(CubeSize)
  );

  var px1 = screenRight.clone().sub(screenOrigin).normalize();

  var px2 = screenLeft.clone().sub(screenOrigin).normalize();

  const rightDistance = px1.distanceToSquared(Right);
  const leftDistance = px2.distanceToSquared(Left);

  return [rightDistance, leftDistance];
}

function calculateAngleOfNormals(cameraToUse) {
  let screenOrigin = toScreenPosition(new THREE.Vector3(0, 0, 0));
  let screenRight = toScreenPosition(
    NORMAL_RIGHT.clone().multiplyScalar(CubeSize)
  );
  let screenLeft = toScreenPosition(
    NORMAL_LEFT.clone().multiplyScalar(CubeSize)
  );

  var px1 = screenRight.clone().sub(screenOrigin).normalize();

  var px2 = screenLeft.clone().sub(screenOrigin).normalize();

  return Math.acos(px1.dot(px2) / (px1.length() * px2.length()));
}

function toScreenPosition(vec3) {
  let widthHalf = window.innerWidth / 2,
    heightHalf = window.innerHeight / 2;
  let pos = vec3.clone();
  pos.project(camera);
  pos.x = pos.x * widthHalf + widthHalf;
  pos.y = -(pos.y * heightHalf) + heightHalf;
  return new THREE.Vector2(pos.x, pos.y);
}

function calculateNormalsForXY(x, y) {
  controls.setRotateStart(500, 500);
  controls.setRotateEnd(500 + x, 500 + y);
  controls.triggerRotate();

  const [distX, distY] = calculateLengthOfNormals(camera);
  const angle = calculateAngleOfNormals(camera);

  return [distX, distY, angle];
}

function findAngle() {
  let minDelta = undefined;
  let minX, minY, minZ, minAngle;
  let minDistX = undefined,
    minDistY = undefined;
  let minArea = undefined;

  let lastIncr = 50;

  minX = 0;
  minY = 0;

  let startX = 0,
    startY = 0;

  let q = 0;

  /*
  for (let incr of [5, 1, 0.1]) {
    console.log("running from", startX - lastIncr, startX + lastIncr);

    for (let x = startX - lastIncr; x < startX + lastIncr; x += incr) {
      for (let y = startY - lastIncr; y < startY + lastIncr; y += incr) {
        const [distX, distY, angle] = calculteDistanceForXY(x, y);

        const delta = Math.abs(LeftRightAngle - angle);
        const area = distX + distY;

        //        console.log("distX", "distY", distX, distY);

        if (minArea == null || area < minArea) {
          minArea = area;
          minDistX = distX;
          minDistY = distY;
          minX = x;
          minY = y;

          minAngle = angle;
          minDelta = delta;
        }
      }
    }
    lastIncr = incr;
    startX = minX;
    startY = minY;
  }
*/
  for (let x = -50; x < 50; x += 5) {
    for (let y = -50; y < 50; y += 5) {
      const [distX, distY, angle] = calculateNormalsForXY(x, y);

      const delta = Math.abs(LeftRightAngle - angle);
      const area = distX + distY;

      if (minArea == null || area < minArea) {
        minArea = area;
        minDistX = distX;
        minDistY = distY;
        minX = x;
        minY = y;

        minAngle = angle;
        minDelta = delta;
      }

      //console.log("angle", MathUtils.radToDeg(angle));
      controls.setRotateStart(500 + x, 500 + y);
      controls.setRotateEnd(500, 500);
      controls.triggerRotate();
    }
  }

  for (let x = minX - 5; x < minX + 5; x += 1) {
    for (let y = minY - 5; y < minY + 5; y += 1) {
      controls.setRotateStart(500, 500);
      controls.setRotateEnd(500 + x, 500 + y);
      controls.triggerRotate();

      const [distX, distY] = calculateLengthOfNormals(camera);
      const angle = calculateAngleOfNormals(camera);
      const delta = Math.abs(LeftRightAngle - angle);
      const area = distX + distY;

      if (minArea == null || area < minArea) {
        minArea = area;
        minDistX = distX;
        minDistY = distY;
        minX = x;
        minY = y;

        minAngle = angle;
        minDelta = delta;
      }

      //console.log("angle", MathUtils.radToDeg(angle));
      controls.setRotateStart(500 + x, 500 + y);
      controls.setRotateEnd(500, 500);
      controls.triggerRotate();
    }
  }

  for (let x = minX - 1; x < minX + 1; x += 0.1) {
    for (let y = minY - 1; y < minY + 1; y += 0.1) {
      controls.setRotateStart(500, 500);
      controls.setRotateEnd(500 + x, 500 + y);
      controls.triggerRotate();

      const [distX, distY] = calculateLengthOfNormals(camera);
      const angle = calculateAngleOfNormals(camera);
      const delta = Math.abs(LeftRightAngle - angle);
      const area = distX + distY;

      //      console.log("distX", "distY", distX, distY);

      if (minArea == null || area < minArea) {
        minArea = area;
        minDistX = distX;
        minDistY = distY;
        minX = x;
        minY = y;

        minAngle = angle;
        minDelta = delta;
      }

      //console.log("angle", MathUtils.radToDeg(angle));
      controls.setRotateStart(500 + x, 500 + y);
      controls.setRotateEnd(500, 500);
      controls.triggerRotate();
    }
  }

  //console.log(
  //  "min angle",
  //  MathUtils.radToDeg(minAngle),
  //  "min distance",
  //  minDistX,
  //  minDistY,
  //  "delta",
  //  MathUtils.radToDeg(minDelta),
  //  minX,
  //  minY
  //);
  controls.setRotateStart(500, 500);
  controls.setRotateEnd(500 + minX, 500 + minY);
  controls.triggerRotate();
  renderer.render(scene, camera);

  render();
}

function getRollOverMeshVisible() {
  if (isInVoxelMode()) {
    return !isKeyDown(ShiftKey) && !isKeyDown(OptionKey);
  }
  return false;
}

function updateUI() {
  let vertexInterfaceVisible = !isInVoxelMode();
  document.body.classList.remove("edit-mode-vertex");
  document.body.classList.remove("edit-mode-preview");
  document.body.classList.remove("edit-mode-voxel");
  document.body.classList.remove("edit-mode-algo");
  if (isInVertexMode()) {
    renderer.domElement.style.opacity = 0;
    imageCanvas.style.opacity = 1;
    rollOverMesh.visible = false;
    document.body.classList.add("edit-mode-vertex");
  } else if (isInAlgoMode()) {
    renderer.domElement.style.opacity = 0;
    imageCanvas.style.opacity = 1;
    rollOverMesh.visible = false;
    document.body.classList.add("edit-mode-algo");
  } else if (isInPreviewMode()) {
    renderer.domElement.style.opacity = 1;
    imageCanvas.style.opacity = 0.5;
    rollOverMesh.visible = false;
    document.body.classList.add("edit-mode-preview");
  } else if (isInVoxelMode()) {
    renderer.domElement.style.opacity = 1;
    if (isResetVisible()) {
      imageCanvas.style.opacity = isKeyDown(KeyQ)
        ? 0.5
        : isKeyDown(KeyW)
        ? 1
        : isDebug()
        ? 0.5
        : 0;
    } else {
      imageCanvas.style.opacity = isKeyDown(KeyQ)
        ? 0
        : isKeyDown(KeyW)
        ? 1
        : 0.5;
    }

    rollOverMesh.visible = getRollOverMeshVisible();
    document.body.classList.add("edit-mode-voxel");
  }
  [interfaceSvg, svgTriangle1, svgTriangle2, svgTriangleOrigin].forEach(
    (elem) => {
      elem.style.display = vertexInterfaceVisible ? "block" : "none";
    }
  );
  if (isInVoxelMode()) {
    if (isKeyDown(ShiftKey)) {
      document.body.style.cursor = "move";
    } else if (isKeyDown(OptionKey)) {
      document.body.style.cursor = "zoom-in";
    } else {
      document.body.style.cursor = "";
    }
  }
}

function drawCurrentImage() {
  ctx.clearRect(0, 0, imageCanvas.width, imageCanvas.height);

  ctx.save();

  const zoomFactor = camera.zoom;

  if (currentImage) {
    imageMinX = (imageCanvas.width - currentImageWidth) / 2;

    imageMinY = (imageCanvas.height - currentImageHeight) / 2;

    imageMinX =
      imageMinX -
      (currentImageWidth / 2) * (zoomFactor - 1) +
      canvasOffsetX * zoomFactor;

    imageMinY =
      imageMinY -
      (currentImageHeight / 2) * (zoomFactor - 1) +
      canvasOffsetY * zoomFactor;

    imageMaxX = imageMinX + currentImageWidth * zoomFactor;
    imageMaxY = imageMinY + currentImageHeight * zoomFactor;

    ctx.drawImage(
      currentImage,
      imageMinX,
      imageMinY,
      currentImageWidth * zoomFactor,
      currentImageHeight * zoomFactor
    );

    /* PREVIOUS */
    /*
    imageMinX = (imageCanvas.width - currentImageWidth) / 2;
    imageMaxX = imageMinX + currentImageWidth;

    imageMinY = (imageCanvas.height - currentImageHeight) / 2;
    imageMaxY = imageMinY + currentImageHeight;

    
    //roundedImage(
    //  ctx,
    //  imageMinX,
    //  imageMinY,
    //  currentImageWidth,
    //  currentImageHeight,
    //  10
    //);
    //ctx.clip();
    ctx.drawImage(
      currentImage,
      imageMinX -
        (currentImageWidth / 2) * (zoomFactor - 1) +
        canvasOffsetX * zoomFactor,
      imageMinY -
        (currentImageHeight / 2) * (zoomFactor - 1) +
        canvasOffsetY * zoomFactor,
      currentImageWidth * zoomFactor,
      currentImageHeight * zoomFactor
    );*/

    const thisConf = {
      canvasOffsetX,
      canvasOffsetY,
      zoomFactor,
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight,
    };

    let canRender = true;
    if (isPointerDown) {
      if (imgData) {
        canRender = false;
      }
    }

    if (
      canRender &&
      JSON.stringify(thisConf) !== JSON.stringify(imgDataRenderConf)
    ) {
      imgData = ctx.getImageData(0, 0, imageCanvas.width, imageCanvas.height);
      imgDataRenderConf = thisConf;
    }
  } else {
    imgData = null;
    imgDataRenderConf = {};
  }

  ctx.restore();
}

function roundedImage(ctx, x, y, width, height, radius) {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  ctx.lineTo(x + radius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
}

function resizeCanvas() {
  imageCanvas.width = window.innerWidth;
  imageCanvas.height = window.innerHeight;
  drawCurrentImage();
}

function setEditMode(mode) {
  editMode = mode;
  updateUI();
  updateResetVisibility();
}

function runAngleAlgo() {
  setEditMode(EditMode.ALGO);

  let corners = getCornerPoints();

  OriginScreen = toVec2(corners.origin);
  Right = toVec2(corners.right).sub(OriginScreen).normalize();
  Left = toVec2(corners.left).sub(OriginScreen).normalize();
  LeftRightAngle = Math.acos(
    Right.dot(Left) / (Right.length() * Left.length())
  );

  const origin = getSvgPosition(svgOrigin);
  const p1 = getSvgPosition(svgP1);
  const p2 = getSvgPosition(svgP2);

  //  console.log({ origin, p1, p2 });

  findAngle();

  const intersect = getScreenPointOnPlane(corners.origin);

  if (intersect) {
    const rounded = intersect.clone();
    rounded.divideScalar(CubeSize).roundToZero().multiplyScalar(CubeSize);

    const roundedOnScreen = toScreenPosition(rounded);

    const deltaOnScreen = roundedOnScreen.sub(toVec2(corners.origin));

    canvasOffsetX = deltaOnScreen.x * camera.zoom;
    canvasOffsetY = deltaOnScreen.y * camera.zoom;

    /*
    setSvgPosition(svgOrigin, {
      x: origin.x - deltaOnScreen.x * 2 + TriangleWidth / 2,
      y: origin.y + deltaOnScreen.y,
    });

    setSvgPosition(svgP1, {
      x: p1.x - deltaOnScreen.x * 2 + TriangleWidth / 2,
      y: p1.y + deltaOnScreen.y,
    });

    setSvgPosition(svgP2, {
      x: p2.x - deltaOnScreen.x * 2 + TriangleWidth / 2,
      y: p2.y + deltaOnScreen.y,
    });*/

    updateSvgLines();

    render();
  }

  setEditMode(EditMode.VOXELS);
  cameraHome = camera.clone();
  cameraRotated = camera.clone();
  canvasOffsetHomeX = canvasOffsetX;
  canvasOffsetHomeY = canvasOffsetY;
  updateUI();
}

window.camera = camera;
